export const CANCEL_PASSPORT_TEMPLATE = `# Cancel {{deceased_first_name}}'s U.S. Passport

This guide helps you notify the State Department of a death and secure the deceased's passport.

When you feel ready, this guide will walk you through the steps to safely return {{deceased_first_name}}'s passport to the State Department for cancellation.

Returning the passport can help protect {{deceased_first_name}}'s identity. (Check official State Department guidelines to confirm current security recommendations).

# What You Need

Gather these items before you begin the mailing process. Here is what you'll want to have ready.

- [ ] {{deceased_first_name}}'s valid U.S. passport book or passport card
- [ ] A certified copy of the death certificate.
- [ ] A short letter asking the State Department to cancel the passport
- [ ] One sturdy, self-addressed envelope.
- [ ] One pre-paid, trackable shipping label (USPS Priority or FedEx).

# What to Do

**Decide on the passport's return:**
- If you want the passport returned: Include an explicit request in your letter.
- If you want it destroyed: No request is needed.

**Prepare for Mailing:**
Use a mailing service with tracking (e.g., USPS Priority Mail) to ensure documents arrive safely.

**Mail all items to the CLASP address:**

U.S. Department of State
Consular Lost and Stolen Passport Unit (CLASP)
44132 Mercure Circle
P.O. Box 1227
Sterling, VA 20166-1227

Save your tracking number and a copy of everything you mailed.

**Note:** Since you'll need a certified copy of the death certificate, it's best to order an extra one specifically for this. It's always safer to keep your original copy tucked away at home.

# Cover Letter Template

[Date]

U.S. Department of State
Consular Lost and Stolen Passport Unit (CLASP)
44132 Mercure Circle
P.O. Box 1227
Sterling, VA 20166-1227

Regarding the U.S. passport for {{deceased_full_name}}

Dear Passport Services,

I am writing to request cancellation of the U.S. passport for {{deceased_full_name}}, who passed away on {{date_of_death}}.

I am enclosing the passport and a certified copy of the death certificate.

I would appreciate it if you could please return the certified death certificate to me at the address below once you have processed the cancellation.

Deceased person's full name: {{deceased_full_name}}
Passport number: {{deceased_passport_number}}
Date of birth: {{date_of_birth}}
Date of death: {{date_of_death}}

Please cancel the passport and return the physical document to me at the address below for family records.

If you need any additional information, please contact me.

Sincerely,
{{requestor_first_name}} {{requestor_last_name}}
{{user_address}}, {{user_city}}, {{user_state}} {{user_zip}}
{{user_phone}}

# Important to Know

- Use a secure, trackable mailing method (such as USPS Priority Mail Express or Registered Mail) so you can confirm delivery.
- There's no need to fill out a Form DS-64 for this -- this letter typically includes the information requested by the State Department. You may need to verify requirements on their official website if there are unique circumstances.
- **Follow-up Contact:** If the processing time passes without word, contact the National Passport Information Center (NPIC): 1-877-487-2778 (TTY: 1-888-874-7793). If you need to check on the status, it helps to have the passport number and your tracking receipt handy.
- If the passport is lost or stolen, report it online immediately via the official State Department Report Lost or Stolen Passport tool for faster, more secure cancellation. Do not mail a paper Form DS-64.
- If there is a dispute regarding the deceased's documents or legal authority, contact a legal professional or Elayne support before mailing original items.

# Quick Checklist

- [ ] Locate the passport book or card
- [ ] Get a certified copy of the death certificate
- [ ] Choose whether you want the passport returned or destroyed
- [ ] Mail everything to CLASP with tracking
- [ ] Save your tracking number and copies of what you sent

Expect the return of the cancelled passport within 8 to 12 weeks. If you have not received the documents back within 12 weeks, contact the National Passport Information Center at 1-877-487-2778. Check the most current administrative windows on the official State Department website: Processing Times Dashboard.`;
